import React, { useCallback, useEffect } from "react";
import { FieldProps } from 'formik';
import { useTranslation } from 'react-i18next';

import { IFieldErrorMessage } from '../../../types/forms.types';
import { FieldLabelKeys, FieldPlaceholderKeys, FieldSubLabelKeys } from '../../../enums/locales/forms';

import { Field } from '../Field';

import { Input as StyledInput } from '../../styled/Input';
import { IProps as IStyledProps } from '../../styled/Input/Input';
import { SiderStepsSuffixes } from "../../../enums/locales/modals";

export interface IPartialStyledProps extends Pick<IStyledProps, 'icon' | 'disabled' | 'onClickIcon' | 'autoComplete'> { }

export interface IProps extends FieldProps, IPartialStyledProps {
  className?: string;
  labelKey?: FieldLabelKeys;
  subLabel?: FieldSubLabelKeys | SiderStepsSuffixes;
  placeholderKey?: FieldPlaceholderKeys,
  noBottomMargin?: boolean;
  onBlur?: (...args: any) => void;
}

const Input: React.FC<IProps> = ({
  field,
  form,
  className = '',
  labelKey,
  subLabel,
  placeholderKey,
  noBottomMargin,
  ...restProps
}) => {
  const { t } = useTranslation();
  const { name } = field;
  const { errors, touched, isSubmitting, setFieldTouched } = form;

  const splitedName = name.split('.')[0];

  const error = errors[splitedName];
  const isTouched = Boolean(touched[splitedName]);
  const isError = Boolean(error) && isTouched;

  useEffect(() => {
    if (isSubmitting && !isTouched) {
      setFieldTouched(splitedName, true);
    }
  }, [isSubmitting, isTouched, splitedName, setFieldTouched]);

  const onBlur = useCallback(() => {
    field.onBlur({ target: { name, value: field.value } });
    setFieldTouched(name, true);
    if (restProps.onBlur) {
      restProps.onBlur({ target: { name, value: field.value } });
    }
  }, [restProps, field]);

  return (
    <Field
      className={className}
      labelKey={labelKey}
      subLabel={subLabel}
      error={error as unknown as IFieldErrorMessage}
      touched={isTouched}
      noBottomMargin={noBottomMargin}
    >
      <StyledInput
        {...field}
        {...restProps}
        onBlur={onBlur}
        error={isError}
        placeholder={placeholderKey && t(placeholderKey)}
      />
    </Field>
  );
};

export default Input;
